import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { toMessage } from "@/api/errors";
import {
  addUserToFolder,
  listFolders,
  removeUserFromFolder,
} from "@/api/lookups";
import {
  type Folder,
  type UUID,
  type UserFolderMembership,
  type UserListItem,
} from "@/api/types";
import { Button } from "@/components/ui/button";

/**
 * `UserFoldersDrawer` — per-user folder-membership management drawer
 * (folder-membership v2, contract §2 + UI brief D, Variant A pass).
 *
 * Mirrors `FolderDrawer`'s overlay + slide-over + Esc + scroll-lock
 * behaviour. Owns its own data fetches: the parent only passes the
 * `UserListItem` it already has (so the header renders synchronously
 * with no flash of empty state), and the drawer:
 *
 *   * Reads `GET /api/folders` to populate the "add" selector.
 *   * Uses `user.folders[]` (the embedded rollup from `/api/users`
 *     v2) as the source-of-truth for current memberships, refetched
 *     transparently after each mutation via
 *     `invalidateQueries(["users"])`.
 *
 * Variant A (2026-05-22): the per-folder role was retired together
 * with migration 0109 — the membership table is now a flat
 * `(folder name + default star + remove button)` list and the "add"
 * form has no role selector. The PATCH endpoint that used to toggle
 * owner/member is gone.
 *
 * RBAC: only admin renders this drawer (the registry hides the
 * affordance for non-admins); the component does not re-check the
 * principal — that's the parent's job. All mutations still propagate
 * 403/409 errors through the inline error slot.
 */
interface UserFoldersDrawerProps {
  open: boolean;
  user: UserListItem | null;
  onClose: () => void;
}

export function UserFoldersDrawer({
  open,
  user,
  onClose,
}: UserFoldersDrawerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>("");
  const [search, setSearch] = useState("");
  const [addFolderId, setAddFolderId] = useState<UUID | "">("");

  // Lock body scroll while open. Mirrors `FolderDrawer`.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Esc closes the drawer.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Reset transient state when the user changes / drawer closes.
  useEffect(() => {
    setError("");
    setAddFolderId("");
    setSearch("");
  }, [user?.id, open]);

  const folders = useQuery({
    queryKey: ["folders", "list"],
    queryFn: () => listFolders(),
    enabled: open,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["users"] });
  };

  const addMutation = useMutation({
    mutationFn: (folderId: UUID) =>
      addUserToFolder(user!.id, { folder_id: folderId }),
    onSuccess: () => {
      setError("");
      setAddFolderId("");
      invalidate();
    },
    onError: (err: unknown) =>
      setError(toMessage(err, t("adminUsers.errors.updateFailed"))),
  });

  const removeMutation = useMutation({
    mutationFn: (folderId: UUID) => removeUserFromFolder(user!.id, folderId),
    onSuccess: () => {
      setError("");
      invalidate();
    },
    onError: (err: unknown) =>
      setError(toMessage(err, t("adminUsers.errors.updateFailed"))),
  });

  // Folders the user is NOT in yet (candidates for the "add" select),
  // narrowed by the search box. Memoised so the option list isn't
  // re-derived on every keystroke when the user is in many folders.
  const currentFolderIds = useMemo(
    () => new Set((user?.folders ?? []).map((f) => f.id)),
    [user?.folders],
  );
  const addCandidates: Folder[] = useMemo(() => {
    const all = (folders.data ?? []).filter((f) => !currentFolderIds.has(f.id));
    if (search.trim() === "") return all;
    const needle = search.trim().toLowerCase();
    return all.filter((f) => f.name.toLowerCase().includes(needle));
  }, [folders.data, currentFolderIds, search]);

  if (!open || user === null) return null;

  const sinceDate = user.created_at.slice(0, 10).split("-").reverse().join(".");
  const memberships: UserFolderMembership[] = user.folders;

  return (
    <div
      className="fixed inset-0 z-50"
      data-testid="user-folders-drawer"
      role="dialog"
      aria-modal="true"
      aria-label={t("adminUsers.drawer.title")}
    >
      <div
        className="absolute inset-0 bg-black/50"
        data-testid="user-folders-drawer-overlay"
        onClick={onClose}
      />
      <aside
        className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col gap-4 overflow-y-auto border-l bg-background p-6 shadow-xl"
        data-testid="user-folders-drawer-panel"
      >
        <header className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">
              {t("adminUsers.drawer.title")}
            </h2>
            <p className="text-sm" data-testid="user-folders-drawer-email">
              <span className="font-mono">{user.email}</span>
              {user.employee_display !== null &&
              user.employee_display !== undefined &&
              user.employee_display !== "" ? (
                <span className="ml-2 text-muted-foreground">
                  · {user.employee_display}
                </span>
              ) : null}
            </p>
            <p className="text-xs text-muted-foreground">
              {user.status === "invited"
                ? t("adminUsers.status.invited")
                : null}
              {user.status === "invited" ? " · " : ""}
              {t("adminUsers.drawer.since", { date: sinceDate })}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid="user-folders-drawer-close"
          >
            {t("adminUsers.drawer.close")}
          </Button>
        </header>

        {error !== "" ? (
          <p
            className="text-xs text-destructive"
            role="alert"
            data-testid="user-folder-error"
          >
            {error}
          </p>
        ) : null}

        {/* ----- Current memberships (Variant A: no role column) ----- */}
        <section className="space-y-2">
          <h3 className="text-base font-semibold">
            {t("adminUsers.drawer.currentHeader")}
          </h3>
          {memberships.length === 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="user-folders-empty"
            >
              {t("adminUsers.drawer.noFolders")}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table
                className="w-full text-sm"
                data-testid="user-folders-table"
              >
                <thead className="bg-muted/40">
                  <tr>
                    <th className="border-b px-3 py-2 text-left font-medium">
                      {t("adminUsers.drawer.columns.folder")}
                    </th>
                    <th className="border-b px-3 py-2 text-left font-medium">
                      {t("adminUsers.drawer.columns.remove")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map((m) => (
                    <tr key={m.id} data-testid={`user-folder-row-${m.id}`}>
                      <td className="border-b px-3 py-2 align-top">
                        {m.is_default ? <span aria-hidden>★ </span> : null}
                        <span>{m.name}</span>
                      </td>
                      <td className="border-b px-3 py-2 align-top">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={removeMutation.isPending}
                          data-testid={`user-folder-remove-${m.id}`}
                          onClick={() => {
                            if (
                              !window.confirm(
                                t("adminUsers.drawer.removeConfirm"),
                              )
                            )
                              return;
                            removeMutation.mutate(m.id);
                          }}
                        >
                          {t("adminUsers.drawer.removeButton")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ----- Add to folder (Variant A: no role selector) ----- */}
        <section className="space-y-2">
          <h3 className="text-base font-semibold">
            {t("adminUsers.drawer.addHeader")}
          </h3>
          {/* The search box stays mounted even when there are no
              candidates so the admin doesn't lose typed input when
              the candidate list updates. */}
          <input
            type="search"
            className="h-8 w-full rounded-md border bg-background px-2 text-sm"
            placeholder={t("adminUsers.drawer.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="user-folder-add-search"
          />
          {addCandidates.length === 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="user-folders-add-empty"
            >
              {t("adminUsers.drawer.noCandidates")}
            </p>
          ) : (
            <form
              className="flex flex-wrap items-end gap-2"
              data-testid="user-folder-add-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (addFolderId === "") return;
                addMutation.mutate(addFolderId);
              }}
            >
              <select
                value={addFolderId}
                onChange={(e) => setAddFolderId(e.target.value as UUID | "")}
                className="h-8 min-w-[12rem] rounded-md border bg-background px-2 text-sm"
                data-testid="user-folder-add-folder"
                required
              >
                <option value="">
                  {t("adminUsers.drawer.folderPlaceholder")}
                </option>
                {addCandidates.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.is_default ? "★ " : ""}
                    {f.name}
                  </option>
                ))}
              </select>
              <Button
                type="submit"
                size="sm"
                disabled={addFolderId === "" || addMutation.isPending}
                data-testid="user-folder-add-submit"
              >
                {t("adminUsers.drawer.addButton")}
              </Button>
            </form>
          )}
        </section>
      </aside>
    </div>
  );
}
